import {DataStatus, EmptyStatus, openDialog} from 'platform/components';
import {Show} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {useRef} from 'react';

import {always, isEmpty, isNil, values} from 'ramda';
import {isNonEmptyArray} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {rentalOrderRoutes, vehiclesRoutes} from '@dms/routes';

import {composePath, useNavigate} from 'shared/web';

import {RentalCalendarActiveFilters} from './components/RentalCalendarActiveFilters';
import {RentalCalendarCreateDialog} from './components/RentalCalendarCreateDialog';
import {RentalCalendarGrid} from './components/RentalCalendarGrid';
import {RentalCalendarHeader} from './components/RentalCalendarHeader';
import {RentalCalendarSlotDialog} from './components/RentalCalendarSlotDialog';
import {useRentalOrderCalendarControl} from './hooks/useRentalOrderCalendarControl';
import {EMPTY_RENTAL_CALENDAR_FILTERS} from './types/RentalCalendarFilterValues';
import type {RentalResourceCalendarEvent} from './types/RentalResourceCalendarEvent';
import {buildCalendarRows} from './utils/buildCalendarRows';
import {getCalendarScale} from './utils/getCalendarScale';
import {getScrollLeftForDate} from './utils/getScrollLeftForDate';
import {groupCalendarRows} from './utils/groupCalendarRows';

/** Tells the target page's breadcrumb to send the user back to the calendar, not to its own list */
const CALENDAR_REDIRECT_SOURCE = 'rental-order-calendar';

export function RentalOrderCalendar() {
  const control = useRentalOrderCalendarControl();
  const navigate = useNavigate();
  const gridScrollRef = useRef<HTMLDivElement>(null);
  const hasActiveFilters = values(control.filters).some(isNonEmptyArray);

  const onNavigate = (direction: 'previous' | 'next' | 'today') => {
    control.onNavigate(direction);

    const grid = gridScrollRef.current;

    if (isNil(grid)) {
      return;
    }

    if (direction === 'today') {
      const today = new Date();
      // The new period has not rendered yet, so the scroll waits for the frame that draws it
      requestAnimationFrame(() => {
        grid.scrollLeft = getScrollLeftForDate(
          today,
          getCalendarScale(control.view, today),
          grid.clientWidth
        );
      });

      return;
    }

    // Every month starts at day 1, so the previous month's scroll offset points at the wrong day
    if (control.view === 'month') {
      grid.scrollLeft = 0;
    }
  };

  /** Details open in their own tab, so the calendar keeps its period, filters and scroll */
  const openInNewTab = (path: string) => {
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  const onVehicleClick = (vehicleId: string) =>
    openInNewTab(
      composePath(vehiclesRoutes.detail, {
        params: {id: vehicleId},
        queryParams: {source: CALENDAR_REDIRECT_SOURCE},
      })
    );

  const onEventClick = (event: RentalResourceCalendarEvent) => {
    // A block has no rental order behind it; it is managed on the vehicle's rental tab, which
    // holds the grid listing every block of that vehicle
    if (event.type === 'block') {
      openInNewTab(
        composePath(vehiclesRoutes.rental, {
          params: {id: event.vehicleId},
          queryParams: {source: CALENDAR_REDIRECT_SOURCE},
        })
      );
      return;
    }

    const redirectRoute = match(event.rentalOrder?.state)
      .with('draft', 'canceled', 'closed', always(rentalOrderRoutes.vehicleAndCustomer))
      .with('reserved', always(rentalOrderRoutes.rentVehicle))
      .with('rented', 'returned', always(rentalOrderRoutes.returnVehicle))
      .with(Pattern.nullish, always(rentalOrderRoutes.vehicleAndCustomer))
      .exhaustive();

    openInNewTab(
      composePath(redirectRoute, {
        params: {id: event.id},
        queryParams: {source: CALENDAR_REDIRECT_SOURCE},
      })
    );
  };

  const onRentalOrderCreated = (rentalOrderId: string) =>
    navigate(
      composePath(rentalOrderRoutes.vehicleAndCustomer, {
        params: {id: rentalOrderId},
        queryParams: {source: CALENDAR_REDIRECT_SOURCE},
      })
    );

  const onSlotClick = (vehicleId: string, slotStart: Date, slotEnd: Date) => {
    const vehicleTitle =
      control.vehicles?.find((vehicle) => vehicle.vehicleId === vehicleId)?.title ?? '';

    // Without the block flow the slot has only one outcome, so it opens the rental form directly
    if (!control.canCreateVehicleBlock) {
      openDialog(
        <RentalCalendarCreateDialog
          data-testid="rentalResourceCalendar"
          vehicleId={vehicleId}
          vehicleTitle={vehicleTitle}
          slotStart={slotStart}
          slotEnd={slotEnd}
          onCreated={onRentalOrderCreated}
        />,
        {
          title: i18n.t('entity.rentalOrder.actions.newRentalOrder'),
          size: 'default',
          'data-testid': 'rentalResourceCalendar-createDialog',
        }
      );

      return;
    }

    openDialog(
      <RentalCalendarSlotDialog
        data-testid="rentalResourceCalendar"
        vehicleId={vehicleId}
        vehicleTitle={vehicleTitle}
        slotStart={slotStart}
        slotEnd={slotEnd}
        onRentalOrderCreated={onRentalOrderCreated}
      />,
      {
        title: i18n.t('general.actions.add'),
        size: 'default',
        'data-testid': 'rentalResourceCalendar-slotDialog',
      }
    );
  };

  const rows = buildCalendarRows(control.vehicles, control.events, control.scale);
  const groups = groupCalendarRows(rows, control.vehicleCategoryLabels);

  return (
    <div className="flex w-full flex-col">
      {/* Toolbar, active filters and the grid share one surface, as the data grid does. Nothing
          above the toolbar may use `overflow-hidden` — it would create a scrollport and stop the
          sticky header from tracking the page scroll. */}
      <div className="flex flex-col rounded bg-palettes-white-10-100 shadow-elevation-1">
        {/* The page is what scrolls here, so the toolbar sticks to the top of it and keeps the
            filter and grouping triggers — and their poppers — reachable */}
        <div className="sticky top-0 z-30 rounded-t bg-palettes-white-10-100 p-3 shadow-elevation-1">
          <RentalCalendarHeader
            data-testid="rentalResourceCalendar"
            currentDate={control.selectedDate}
            view={control.view}
            onNavigate={onNavigate}
            onDateChange={control.onDateChange}
            onViewChange={control.onViewChange}
            onRefresh={control.onRefresh}
            filters={control.filters}
            onFiltersChange={control.onFiltersChange}
          />
        </div>
        <RentalCalendarActiveFilters
          data-testid="rentalResourceCalendar"
          filters={control.filters}
          onFiltersChange={control.onFiltersChange}
        />
        <div className="flex flex-col">
          {/*
          Its own rail above the grid rather than an overlay, which would sit on the sticky column
          headers. The height is always reserved so the calendar does not shift as it appears.
          Platform's progress bar only takes severity colours and its blue reads as an alert here,
          so this shares the skeletons' neutral grey and both loading states look like one thing.
        */}
          <div className="h-1 shrink-0">
            <Show when={control.isRefreshing}>
              <div
                className="size-full animate-pulse bg-palettes-neutral-50-100"
                data-testid="rentalResourceCalendar-refreshing"
              />
            </Show>
          </div>
          <div>
            <DataStatus isLoading={control.isLoading} isError={control.isError} minHeight={100}>
              <div ref={gridScrollRef} className="relative flex w-full flex-col overflow-x-auto">
                <RentalCalendarGrid
                  data-testid="rentalResourceCalendar"
                  scale={control.scale}
                  groups={groups}
                  blockReasonLabels={control.blockReasonLabels}
                  areEventsPending={control.areEventsPending}
                  onEventClick={onEventClick}
                  onSlotClick={onSlotClick}
                  onDayDrillDown={control.onDayDrillDown}
                  onVehicleClick={onVehicleClick}
                />
                {/* Sticky so it centres on the viewport, inside the scroller so its scrollbar sits below */}
                <Show when={isEmpty(rows)}>
                  <div className="sticky left-0 flex flex-1 items-center justify-center p-8">
                    <EmptyStatus
                      variant="vehicle"
                      headline={
                        hasActiveFilters
                          ? i18n.t('general.labels.noSearchResults')
                          : i18n.t('entity.vehicle.notifications.noVehicles')
                      }
                      action={
                        hasActiveFilters
                          ? {
                              title: i18n.t('page.datagrid.labels.resetAllFilters'),
                              variant: 'link',
                              onClick: () => control.onFiltersChange(EMPTY_RENTAL_CALENDAR_FILTERS),
                            }
                          : undefined
                      }
                      data-testid="rentalResourceCalendar-resourceView-empty"
                    />
                  </div>
                </Show>
              </div>
            </DataStatus>
          </div>
        </div>
      </div>
    </div>
  );
}
